import React, { useState, useEffect } from "react";
import {
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Group,
  Text,
  Alert,
  Divider,
  Loader,
  Center,
  SimpleGrid,
} from "@mantine/core";
import { IconAlertCircle, IconBrandWindows, IconLock } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { isDevMode } from "../lib/isDevMode";
import { notifications } from "@mantine/notifications";
import { AuthLayout } from "../components/AuthLayout";
import { BRAND_MEDIUM } from "../theme/brand";

const ERROR_MESSAGES: Record<string, string> = {
  entra_config: "Microsoft sign-in is not configured. Please contact support.",
  invalid_callback: "Sign-in was cancelled or the session expired. Please try again.",
  no_email: "Your account did not provide an email. Please use a work account.",
  not_authorized: "Your account is not authorized to access this application.",
  callback_failed: "Sign-in failed. Please try again.",
};

interface LoginForm {
  email: string;
  password: string;
}

const DEMO_ACCOUNTS = [
  { role: "admin", label: "Admin", email: "admin@hmh.mv" },
  { role: "procurement", label: "Procurement (Mohamed)", email: "procurement@hmh.mv" },
  { role: "procurement2", label: "Procurement (Fathimath)", email: "procurement2@hmh.mv" },
  { role: "procurement3", label: "Procurement (Ali)", email: "procurement3@hmh.mv" },
  { role: "headOfProcurement", label: "Head of Procurement", email: "headofprocurement@hmh.mv" },
  { role: "committee", label: "Committee (Ahmed)", email: "committee@hmh.mv" },
  { role: "committee2", label: "Committee (Aishath)", email: "committee2@hmh.mv" },
  { role: "committee3", label: "Committee (Ibrahim)", email: "committee3@hmh.mv" },
  { role: "committee4", label: "Committee (Mariyam)", email: "committee4@hmh.mv" },
  { role: "committee5", label: "Committee (Hussain)", email: "committee5@hmh.mv" },
  { role: "ceo", label: "CEO", email: "ceo@hmh.mv" },
  { role: "viewer", label: "Viewer", email: "viewer@hmh.mv" },
] as const;

const DEMO_EMAIL_BY_ROLE: Record<string, string> = DEMO_ACCOUNTS.reduce(
  (acc, account) => {
    acc[account.role] = account.email;
    return acc;
  },
  {} as Record<string, string>
);

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, loginWithMicrosoft, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Already logged in: redirect to dashboard (avoid showing login form)
  React.useEffect(() => {
    if (authLoading || !user) return;
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
    navigate(from ?? "/dashboard", { replace: true });
  }, [authLoading, user, navigate, location.state]);

  const form = useForm<LoginForm>({
    initialValues: { email: "", password: "" },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      password: (value) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
    },
  });

  useEffect(() => {
    const err = searchParams.get("error");
    if (err) setError(ERROR_MESSAGES[err] || "Sign-in failed. Please try again.");
  }, [searchParams]);

  const handleSubmit = async (values: LoginForm) => {
    if (!login) return;
    setLoading(true);
    setError("");
    try {
      await login(values.email, values.password);
      notifications.show({ title: "Success", message: "Welcome back!", color: "green" });
      navigate("/dashboard");
    } catch (err: any) {
      const msg = err.message || "Login failed. Please try again.";
      setError(msg);
      notifications.show({ title: "Error", message: msg, color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (role: string) => {
    if (!login) {
      setError("Development login is not available. Run the web app with npm run dev:web.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const user = await login(DEMO_EMAIL_BY_ROLE[role] ?? DEMO_EMAIL_BY_ROLE.viewer, "password");
      notifications.show({
        title: "Demo Login",
        message: `Logged in as ${user?.fullName} (${user?.role})`,
        color: "blue",
      });
      navigate("/dashboard");
    } catch (err: any) {
      setError(err.message || "Demo login failed.");
      notifications.show({ title: "Error", message: "Demo login failed.", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const isDev = isDevMode();

  // Logged in: show minimal redirect state (effect will navigate)
  if (!authLoading && user) {
    return (
      <Center mih="100vh">
        <Group gap="sm">
          <Loader size="sm" color={BRAND_MEDIUM} />
          <Text c="dimmed">Redirecting to your dashboard…</Text>
        </Group>
      </Center>
    );
  }

  return (
    <AuthLayout
      product="HMH Procurement"
      panelEyebrow="Hulhumalé Hospital · Staff portal"
      panelTitle="Procurement, from request to contract"
      panelPoints={[
        "Tenders, evaluations and bid committee meetings",
        "Single source forms and project summary sheets",
        "Approvals, contracts and a full audit trail",
      ]}
      title="Sign in"
      subtitle="Use your hospital Microsoft account to continue."
      back={{ label: "Back to home", to: "/" }}
    >
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md" role="alert">
          {error}
        </Alert>
      )}

      <Button
        leftSection={<IconBrandWindows size={20} />}
        size="lg"
        fullWidth
        color={BRAND_MEDIUM}
        radius="md"
        loading={loading}
        onClick={loginWithMicrosoft}
      >
        Sign in with Microsoft
      </Button>

      <Group gap={6} wrap="nowrap" justify="center">
        <IconLock size={14} color="var(--mantine-color-dimmed)" aria-hidden />
        <Text size="xs" c="dimmed">
          For authorised Hulhumalé Hospital staff only. Sign-in problems? Contact the IT Department.
        </Text>
      </Group>

      {isDev && (
        <Stack gap="md" mt="sm">
          <Divider label="Development only" labelPosition="center" />
          <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="sm">
              <TextInput
                label="Email"
                placeholder="your@email.com"
                type="email"
                autoComplete="username"
                required
                radius="md"
                {...form.getInputProps("email")}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                autoComplete="current-password"
                required
                radius="md"
                {...form.getInputProps("password")}
              />
              <Button type="submit" loading={loading} fullWidth variant="light" color={BRAND_MEDIUM} radius="md">
                Sign in (dev)
              </Button>
            </Stack>
          </form>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Demo accounts
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {DEMO_ACCOUNTS.map(({ role, label }) => (
              <Button
                key={role}
                variant="default"
                size="xs"
                onClick={() => handleDemoLogin(role)}
                disabled={loading}
                radius="md"
              >
                {label}
              </Button>
            ))}
          </SimpleGrid>
        </Stack>
      )}
    </AuthLayout>
  );
};
